import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { venuesApi } from "../services/venues";
import { useAndroidBack } from "../hooks/useAndroidBack";
import { useFitText } from "../hooks/useFitText";
import { useKeyboardOpen } from "../hooks/useKeyboardOpen";
import { nearbyPosition } from "../lib/venueLocation";
import type { VenueResult } from "../types/venues";
import { t } from "../i18n";
import "./VenueSearchScreen.css";

// "Where do you perform?" — the full-screen venue search the Venue box in Edit
// profile opens (founder pick A, 2026-10-23: Instagram's "Add location"). The
// whole screen is for results, so the keyboard never hides them; on a computer
// it is a centred window. In the phone apps (1.5.3) the search box stays put
// under the top bar and only the results scroll, ending on the keyboard's top
// edge (VenueSearchScreen.css); Android's back button closes it.
//
// Every path ends in a save, never an error screen: Google's places, or
// "Use “what I typed”" for a place Google doesn't know — which is also all
// that shows when Google is off, down or slow (the server answers no results).
// That row waits for Google: under the places once they're in, alone only
// after USE_TYPED_AFTER_MS with none (founder 2026-10-29: alone at the top
// while the places were still coming, it looked strange).
export const USE_TYPED_AFTER_MS = 3000;

export const VENUE_PIN = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const PEN = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);
const SEARCH = (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </svg>
);

// Google's required credit when its places show without a map. A brand name,
// the same in every language, so it is not a translation key.
const GOOGLE_CREDIT = "Google Maps";

// Google bills a whole search as one session when it ends in a pick.
function newSession(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
}

type Props = {
  /** The venue on the profile now. */
  current: { name: string; placeId: string | null };
  onPick: (venue: { placeId?: string; name: string; session: string }) => void;
  onRemove: () => void;
  onClose: () => void;
};

export default function VenueSearchScreen({ current, onPick, onRemove, onClose }: Props) {
  // A typed venue opens with its text already searched, so one tap makes it
  // a real place; a picked one opens empty, with itself on top.
  const [query, setQuery] = useState(current.placeId ? "" : current.name);
  const [results, setResults] = useState<VenueResult[]>([]);
  // True once the text has sat USE_TYPED_AFTER_MS with no places to show.
  const [waited, setWaited] = useState(false);
  const session = useRef(newSession()).current;
  const inputRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  // One whole line, down to 12 px on the smallest phones in a wide font.
  useFitText(titleRef, t("venueSearch.title"), 12);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useAndroidBack(() => closeRef.current());
  const keyboardUp = useKeyboardOpen();
  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      // Near the talent once their rough position is in (lib/venueLocation);
      // until then, their country, as always.
      venuesApi
        .search(q, session, ctrl.signal, nearbyPosition())
        .then((r) => setResults(Array.isArray(r?.results) ? r.results : []))
        .catch(() => {
          if (!ctrl.signal.aborted) setResults([]);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, session]);

  const typed = query.trim();

  useEffect(() => {
    setWaited(false);
    if (!typed) return;
    const timer = setTimeout(() => setWaited(true), USE_TYPED_AFTER_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  return createPortal(
    <div className={`vs-overlay${keyboardUp ? " vs-overlay--kb" : ""}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="vs-screen" role="dialog" aria-modal="true" aria-label={t("venueSearch.title")}>
        <div className="vs-top">
          <button type="button" className="vs-back" onClick={onClose} aria-label={t("venueSearch.back")}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <span ref={titleRef} className="vs-title">{t("venueSearch.title")}</span>
          <span />
        </div>
        <div className="vs-search">
          {SEARCH}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value.slice(0, 50))}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            placeholder={t("venueSearch.searchVenues")}
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={50}
          />
          {query && (
            <button type="button" className="vs-clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label={t("venueSearch.clear")}>
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="10" fill="currentColor" />
                <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="#161a26" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <div className="vs-body">
          <div className="vs-list">
            {!typed && current.name && (
              <div className="vs-row vs-row--current">
                <span className={`vs-ic${current.placeId ? " vs-ic--on" : ""}`}>{VENUE_PIN}</span>
                <span className="vs-text">
                  <span className="vs-name">{current.name}</span>
                </span>
                <button type="button" className="vs-remove" onClick={onRemove}>
                  {t("venueSearch.remove")}
                </button>
              </div>
            )}
            {typed &&
              results.map((r) => (
                <button type="button" key={r.placeId} className="vs-row" onClick={() => onPick({ placeId: r.placeId, name: r.name, session })}>
                  <span className="vs-ic">{VENUE_PIN}</span>
                  <span className="vs-text">
                    <span className="vs-name">{r.name}</span>
                    {r.address && <span className="vs-addr">{r.address}</span>}
                  </span>
                </button>
              ))}
            {typed && (results.length > 0 || waited) && (
              <button type="button" className="vs-row" onClick={() => onPick({ name: typed, session })}>
                <span className="vs-ic">{PEN}</span>
                <span className="vs-text">
                  <span className="vs-name">{t("venueSearch.useTyped", { name: typed })}</span>
                </span>
              </button>
            )}
          </div>
          {typed && results.length > 0 && <div className="vs-google">{GOOGLE_CREDIT}</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
